import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ICallFlowStep, IClinicInternalExtension } from '@dataTypes/clinic.type'
import StepPanelShell from '../StepPanelContainer.component'
import useMenuIVR, {
  MENU_IVR_ACTION_OPTIONS,
  MENU_IVR_COLLECT_INPUT_OPTIONS,
  MENU_IVR_DIAL_PAD_KEYS,
  MenuIvrActionValue,
} from './useMenuIVR'
import './MenuIVRPanelStyle.css'
import {
  ActionButton,
  Button,
  InputField,
  Select,
} from '@enamelai/design-system'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'

export type MenuIVRPanelProps = {
  step: Extract<ICallFlowStep, { type: 'menu_ivr' }>
  branchLabel: string
  internalExtensions: IClinicInternalExtension[]
  aiEnabled?: boolean
  onChange: (
    patch: Partial<Extract<ICallFlowStep, { type: 'menu_ivr' }>>,
  ) => void
  onCancel: () => void
  onDone: () => void
}

const TTS_PLACEHOLDER = 'Hello, thanks for calling. Press 0 for assistance.'

const TTS_REQUIRED_MSG = 'Preview text-to-speech is required.'
const KEYS_REQUIRED_MSG = 'At least one dial key action is required.'
const KEY_DIGIT_REQUIRED_MSG = 'Each key row must have a dial number.'
const KEY_EXT_REQUIRED_MSG =
  'Call ext.: extension number is required for dial key {digit}.'
const KEY_COLLECT_REQUIRED_MSG =
  'Collect info: input is required for dial key {digit}.'
const KEY_ROUND_ROBIN_REQUIRED_MSG =
  'Round robin: team is required for dial key {digit}.'

const validateMenuIvrSave = (
  step: MenuIVRPanelProps['step'],
): Record<string, string> => {
  const errors: Record<string, string> = {}
  if (!step.ttsText?.trim()) {
    errors.ttsText = TTS_REQUIRED_MSG
  }
  const keys = step.keys ?? []
  if (keys.length === 0) {
    errors.keys = KEYS_REQUIRED_MSG
  }
  for (const k of keys) {
    const digit = k.digit?.trim() || '(unset)'
    if (!k.digit?.trim()) {
      errors.digit = KEY_DIGIT_REQUIRED_MSG
    }
    const meta = k.meta ?? {}
    const action = k.action
    if (action === 'call_ext' && !meta.extension?.trim()) {
      errors[digit] = KEY_EXT_REQUIRED_MSG.replace('{digit}', digit)
    }
    if (action === 'collect_info' && !meta.input?.trim()) {
      errors[digit] = KEY_COLLECT_REQUIRED_MSG.replace('{digit}', digit)
    }
    if (action === 'round_robin' && !meta.team?.trim()) {
      errors[digit] = KEY_ROUND_ROBIN_REQUIRED_MSG.replace('{digit}', digit)
    }
  }
  return errors
}

const buildMenuIvrExtensionOptions = (
  internalExtensions: IClinicInternalExtension[],
): { label: string; value: string }[] => {
  const seen = new Set<string>()
  const opts: { label: string; value: string }[] = []

  const add = (label: string, value: string): void => {
    const v = value.trim()
    if (!v || seen.has(v)) return
    seen.add(v)
    opts.push({ label: label.trim() || v, value: v })
  }

  for (const e of internalExtensions) {
    if (!e.extension_number?.trim()) continue
    const ext = e.extension_number.trim()
    add(ext, ext)
  }

  opts.sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { numeric: true }),
  )
  return opts
}

const MenuIVRPanel = (props: MenuIVRPanelProps): React.ReactElement => {
  const { teams } = useTeamCatalog()
  const hooks = useMenuIVR(props)
  const [saveError, setSaveError] = useState<Record<string, string>>({})
  const actionOptions = useMemo(
    () =>
      MENU_IVR_ACTION_OPTIONS.filter(
        (option) =>
          option.value !== 'ai_receptionist' || props.aiEnabled === true,
      ),
    [props.aiEnabled],
  )

  const ivrExtensionBaseOptions = useMemo(
    () => buildMenuIvrExtensionOptions(props.internalExtensions),
    [props.internalExtensions],
  )
  const extensionNumberById = useMemo(() => {
    const map = new Map<string, string>()
    for (const e of props.internalExtensions) {
      const id = e.clinic_internal_extension_id?.trim()
      const ext = e.extension_number?.trim()
      if (!id || !ext) continue
      map.set(id, ext)
    }
    return map
  }, [props.internalExtensions])

  useEffect(() => {
    const keys = props.step.keys ?? []
    let changed = false
    const nextKeys = keys.map((k) => {
      if (k.action !== 'call_ext') return k
      const rawExt = k.meta?.extension?.trim()
      if (!rawExt) return k
      const mappedExt = extensionNumberById.get(rawExt)
      if (!mappedExt) return k
      changed = true
      return {
        ...k,
        meta: {
          ...(k.meta ?? {}),
          extension: mappedExt,
        },
      }
    })
    if (changed) {
      props.onChange({ keys: nextKeys })
    }
  }, [props.step.keys, props.onChange, extensionNumberById])

  const handleDone = useCallback(() => {
    const errors = validateMenuIvrSave(props.step)
    if (Object.keys(errors).length > 0) {
      setSaveError(errors)
      return
    }
    setSaveError({})
    props.onDone()
  }, [props])

  const renderExtraField = (
    index: number,
    action: MenuIvrActionValue,
    meta: Record<string, string>,
  ): React.ReactNode => {
    const extraKey = `menu-ivr-extra-${index}-${action}`
    if (action === 'call_ext') {
      const curExt = meta.extension?.trim() ?? ''
      const extDataSource =
        curExt && !ivrExtensionBaseOptions.some((o) => o.value === curExt)
          ? [{ label: curExt, value: curExt }, ...ivrExtensionBaseOptions]
          : ivrExtensionBaseOptions
      return (
        <div key={extraKey} className="scp-ivr-dial-col">
          <Select
            label="Extension no."
            options={extDataSource}
            value={curExt || undefined}
            placeholder={
              extDataSource.length === 0
                ? 'No extensions in phone management'
                : 'Select extension'
            }
            onChange={(value) => {
              if (Object.keys(saveError).length > 0) setSaveError({})
              hooks.updateKeyRow(index, {
                meta: { ...meta, extension: String(value || '') },
              })
            }}
          />
        </div>
      )
    }
    if (action === 'collect_info') {
      return (
        <div key={extraKey} className="scp-ivr-extras">
          <Select
            label="Input"
            options={MENU_IVR_COLLECT_INPUT_OPTIONS}
            value={meta.input || undefined}
            onChange={(value) => {
              hooks.updateKeyRow(index, {
                meta: { ...meta, input: String(value || '') },
              })
              if (Object.keys(saveError).length > 0) setSaveError({})
            }}
          />
        </div>
      )
    }
    if (action === 'round_robin') {
      return (
        <div key={extraKey} className="scp-ivr-extras">
          <Select
            label="Team"
            options={teams.map((team) => ({
              label: team,
              value: team,
            }))}
            value={meta.team || undefined}
            onChange={(value) => {
              hooks.updateKeyRow(index, {
                meta: { ...meta, team: String(value || '') },
              })
              if (Object.keys(saveError).length > 0) setSaveError({})
            }}
          />
        </div>
      )
    }
    return null
  }

  return (
    <StepPanelShell
      title="Menu (IVR)"
      branchLabel={props.branchLabel}
      confirmLabel="Save"
      onCancel={props.onCancel}
      onDone={handleDone}
    >
      <div className="scp-menu-ivr-panel">
        <div className="scp-panel-field scp-menu-ivr-tts-block">
          <InputField
            className="scp-menu-ivr-textarea"
            variant="message"
            fullWidth
            showCounter={false}
            label="Preview text-to-speech audio"
            value={props.step.ttsText ?? ''}
            placeholder={TTS_PLACEHOLDER}
            error={!!saveError.ttsText}
            helperText={saveError.ttsText}
            onChange={(event) => {
              if (
                Object.keys(saveError).length > 0 &&
                event.target.value.trim()
              )
                setSaveError(validateMenuIvrSave(props.step))
              props.onChange({ ttsText: event.target.value })
            }}
            clearable={false}
          />
        </div>

        <div className="scp-panel-field scp-menu-ivr-tts-block">
          <span className="scp-panel-label">Assign actions</span>
          <div className="scp-ivr-dial-pad" role="group">
            {MENU_IVR_DIAL_PAD_KEYS.map((d) => {
              const on = hooks.isDigitAssigned(d)
              return (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant="ghost"
                  className={`scp-ivr-dial-pad-key${on ? ' scp-ivr-dial-pad-active-key' : ''}`}
                  onClick={() => hooks.addKeyForDigit(d)}
                >
                  {d}
                </Button>
              )
            })}
          </div>
        </div>
        {saveError.keys && <span className="hint-text">{saveError.keys}</span>}

        <div className="scp-ivr-config-table">
          <div className="scp-ivr-config-rows">
            {hooks.keyRows.map((row, index) => (
              <Fragment key={`${row.digit}-${index}`}>
                <div
                  className={
                    ['go_to_menu', 'ai_receptionist'].includes(row.action)
                      ? 'scp-ivr-config-row scp-ivr-config-row--wide'
                      : 'scp-ivr-config-row'
                  }
                >
                  <div className="scp-ivr-dial-col">
                    <Select
                      label="Dial no."
                      options={hooks.digitOptionsForRow(index)}
                      value={row.digit || undefined}
                      placeholder="—"
                      onChange={(value) => {
                        const next = String(value || '')
                        if (next) hooks.updateKeyRow(index, { digit: next })
                      }}
                    />
                  </div>
                  <div className="scp-ivr-action-col">
                    <Select
                      label="Action"
                      options={actionOptions}
                      value={row.action}
                      onChange={(value) => {
                        if (Object.keys(saveError).length > 0) setSaveError({})
                        const next =
                          (String(value || '') as MenuIvrActionValue) ||
                          'call_ext'
                        hooks.updateKeyRow(index, { action: next })
                      }}
                    />
                  </div>
                  {renderExtraField(index, row.action, row.meta) ?? (
                    <div
                      key={`menu-ivr-extra-${index}-${row.action}-empty`}
                      className="scp-ivr-extras scp-ivr-extras--empty"
                    />
                  )}
                  <ActionButton
                    size="sm"
                    tone="danger"
                    variant="outline"
                    label="Remove"
                    className="scp-ivr-config-remove"
                    icon={
                      <CloseIcon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    }
                    onClick={() => hooks.removeKeyRow(index)}
                  />
                </div>
                {saveError[row.digit] && (
                  <span className="hint-text">{saveError[row.digit]}</span>
                )}
              </Fragment>
            ))}
          </div>
          <div className="scp-bottom-button-wrapper scp-ivr-add-wrap">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="scp-ivr-add-number"
              onClick={() => {
                if (Object.keys(saveError).length > 0)
                  setSaveError(validateMenuIvrSave(props.step))
                hooks.addKeyRow()
              }}
            >
              Add number
            </Button>
          </div>
        </div>
      </div>
    </StepPanelShell>
  )
}

export default withTeamCatalog(MenuIVRPanel)
